import React, { useEffect, useState } from 'react';
import {
  TrendingUp, MousePointerClick, Smartphone, Globe, 
  BarChart, ArrowRight, Zap, Target, Users, Sparkles,
  Search, Share2, Rocket
} from 'lucide-react';
import SEO from './SEO';

const DigitalMarketing = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  return (
    <div className="bg-[#050810] min-h-screen text-white overflow-hidden selection:bg-[#0EA5E9]/30" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Digital Marketing Services in Sangli – Vishwa DigiTech"
        description="Boost your business with Vishwa DigiTech's digital marketing services in Sangli. Expert SEO, Google Ads, Meta Ads, content marketing & online growth strategies for businesses across India."
        keywords="digital marketing sangli, SEO services sangli, google ads sangli, meta ads india, content marketing, online marketing sangli maharashtra, digital marketing agency india"
        canonicalPath="/services/marketing"
      />
      
      {/* Custom Animations & Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-float { animation: float 7s ease-in-out infinite; }
        .animate-float-delayed { animation: float 7s ease-in-out 3.5s infinite; }
        .animate-marquee { animation: marquee 25s linear infinite; }
        
        .bento-card {
          background: rgba(10, 15, 31, 0.8);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255,255,255,0.05);
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .bento-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px -10px rgba(14, 165, 233, 0.2), inset 0 0 0 1px rgba(14, 165, 233, 0.5);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ffffff 0%, #0EA5E9 50%, #06B6D4 100%);
          background-size: 200% auto;
          color: #fff;
          background-clip: text;
          text-fill-color: transparent;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shine 4s linear infinite;
        }
        
        @keyframes shine {
          to { background-position: 200% center; }
        }
      `}</style>

      {/* Decorative Background Orbs */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden flex justify-center items-center">
        <div className="absolute top-[10%] left-[5%] w-96 h-96 bg-[#0EA5E9]/10 rounded-full blur-[120px] animate-float" />
        <div className="absolute bottom-[20%] right-[5%] w-[500px] h-[500px] bg-[#1A4A8B]/20 rounded-full blur-[150px] animate-float-delayed" />
        <div className="absolute top-[40%] right-[30%] w-[300px] h-[300px] bg-[#06B6D4]/10 rounded-full blur-[100px] animate-float" />
      </div>

      <div className="relative z-10">
        
        {/* HERO SECTION */}
        <section className="pt-32 pb-20 px-4 max-w-7xl mx-auto flex flex-col items-center text-center">
          
          <div className={`transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 shadow-sm backdrop-blur-md mb-10 hover:shadow-lg transition-shadow cursor-default">
              <Sparkles className="w-4 h-4 text-[#0EA5E9]" />
              <span className="text-sm font-bold tracking-widest text-[#0EA5E9] uppercase">Digital Growth Agency</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-8 leading-[1.1] text-white">
              Grow Your Business with <br className="hidden lg:block"/>
              <span className="gradient-text">Result-Driven Digital Marketing</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto font-light mb-12 leading-relaxed">
              We help businesses increase sales, generate quality leads, and build a strong online presence through proven digital marketing strategies.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full sm:w-auto">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#0284c7] text-white rounded-xl font-medium text-lg hover:from-[#0284c7] hover:to-[#0369a1] transition-colors shadow-[0_0_20px_rgba(14,165,233,0.3)] flex items-center justify-center gap-3 overflow-hidden w-full sm:w-auto">
                <span className="relative z-10">Get Free Consultation</span> 
                <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="group px-8 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-lg hover:border-[#0EA5E9]/50 hover:bg-white/10 transition-colors flex items-center justify-center gap-3 w-full sm:w-auto shadow-sm">
                <Target className="w-5 h-5 text-[#0EA5E9] group-hover:scale-110 transition-transform" /> View Services
              </button>
            </div>
          </div>
        </section>

        {/* INFINITE MARQUEE */}
        <section className="py-10 border-y border-white/5 bg-[#0A0F1F]/40 backdrop-blur-sm overflow-hidden flex">
          <div className="flex whitespace-nowrap animate-marquee items-center">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-16 px-8 items-center">
                <span className="text-2xl font-black text-gray-500 uppercase tracking-widest">Growth</span>
                <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" />
                <span className="text-2xl font-black text-gray-500 uppercase tracking-widest">Innovation</span>
                <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" />
                <span className="text-2xl font-black text-gray-500 uppercase tracking-widest">Strategy</span>
                <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" />
                <span className="text-2xl font-black text-gray-500 uppercase tracking-widest">Performance</span>
                <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" />
                <span className="text-2xl font-black text-gray-500 uppercase tracking-widest">Results</span>
                <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" />
              </div>
            ))}
          </div>
        </section>

        {/* BENTO GRID SERVICES */}
        <section className="py-32 px-4 max-w-7xl mx-auto">
          <div className="mb-20 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Our Capabilities</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">End-to-end digital marketing solutions engineered to turn visibility into scalable revenue.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[320px]">
            
            {/* 1. SEO & Organic (Col Span 2) */}
            <div className="bento-card rounded-3xl p-8 md:col-span-2 relative overflow-hidden group">
              <div className="absolute -right-20 -top-20 w-64 h-64 bg-[#0EA5E9]/10 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors duration-500">
                    <Search className="w-7 h-7" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4 text-white">Advanced SEO Strategy</h3>
                  <p className="text-gray-400 text-lg leading-relaxed max-w-md">We don't just guess keywords. We execute deep technical audits, semantic content clustering, and high-authority backlinking to guarantee top rankings.</p>
                </div>
                
                {/* Decorative Chart */}
                <div className="absolute bottom-0 right-0 w-1/2 h-1/2 opacity-20 group-hover:opacity-60 group-hover:translate-y-[-10px] transition-all duration-500">
                  <div className="flex items-end gap-3 h-full p-6">
                    <div className="w-1/4 bg-[#0EA5E9]/40 rounded-t-xl h-1/3 border-t border-[#0EA5E9]"></div>
                    <div className="w-1/4 bg-[#0EA5E9]/60 rounded-t-xl h-1/2 border-t border-[#0EA5E9]"></div>
                    <div className="w-1/4 bg-[#0EA5E9]/80 rounded-t-xl h-3/4 border-t border-[#0EA5E9]"></div>
                    <div className="w-1/4 bg-[#0EA5E9] rounded-t-xl h-full border-t border-white/50"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Paid Ads (Square) */}
            <div className="bento-card rounded-3xl p-8 relative overflow-hidden group flex flex-col justify-between">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors duration-500">
                <Target className="w-7 h-7" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">Performance Ads</h3>
                <p className="text-gray-400 leading-relaxed">Laser-targeted PPC campaigns on Google, Meta & LinkedIn. We focus purely on reducing CPA and scaling ROAS.</p>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#0EA5E9]/20 rounded-full blur-2xl opacity-40 group-hover:scale-150 transition-transform duration-700 pointer-events-none" />
            </div>

            {/* 3. Social Media (Square) */}
            <div className="bento-card rounded-3xl p-8 relative overflow-hidden group flex flex-col justify-between">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors duration-500">
                <Share2 className="w-7 h-7" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">Viral Social Media</h3>
                <p className="text-gray-400 leading-relaxed">From trending Reels to aesthetic grid layouts, we build cult-like communities that actively engage with your brand.</p>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#0EA5E9]/20 rounded-full blur-2xl opacity-40 group-hover:scale-150 transition-transform duration-700 pointer-events-none" />
            </div>

            {/* 4. Content Marketing (Square) */}
            <div className="bento-card rounded-3xl p-8 relative overflow-hidden group flex flex-col justify-between">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors duration-500">
                <Sparkles className="w-7 h-7" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">Content Strategy</h3>
                <p className="text-gray-400 leading-relaxed">High-converting copywriting, authority-building blogs, and PR articles that position you as the industry leader.</p>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#0EA5E9]/20 rounded-full blur-2xl opacity-40 group-hover:scale-150 transition-transform duration-700 pointer-events-none" />
            </div>
            
            {/* 5. Email Marketing (Square) */}
            <div className="bento-card rounded-3xl p-8 relative overflow-hidden group flex flex-col justify-between">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors duration-500">
                <MousePointerClick className="w-7 h-7" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">Email Automation</h3>
                <p className="text-gray-400 leading-relaxed">Automated drip campaigns and newsletter sequences that nurture cold leads into loyal, repeat customers.</p>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#0EA5E9]/20 rounded-full blur-2xl opacity-40 group-hover:scale-150 transition-transform duration-700 pointer-events-none" />
            </div>

            {/* 6. Conversion Rate Optimization (Col Span 3) */}
            <div className="bento-card rounded-3xl p-8 md:col-span-3 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0EA5E9]/5 to-transparent pointer-events-none" />
              <div className="relative z-10 h-full flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1">
                  <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors duration-500">
                    <TrendingUp className="w-7 h-7" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4 text-white">Conversion Rate Optimization (CRO)</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">Traffic means nothing without conversions. We use heatmap analysis, A/B testing, and behavioral psychology to turn passive visitors into paying clients at the highest possible rate.</p>
                </div>
                
                {/* Interactive UI Element */}
                <div className="flex-1 w-full bg-[#050810] rounded-2xl shadow-xl border border-white/10 p-4 transform group-hover:-translate-y-2 transition-transform duration-500 relative overflow-hidden flex items-center justify-center min-h-[200px]">
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0EA5E9]/10 to-transparent"></div>
                  
                  {/* Mock A/B Test Graphic */}
                  <div className="flex gap-4 w-full px-4 relative z-10">
                    <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center">
                      <span className="text-xs text-gray-500 mb-2">Variant A</span>
                      <div className="text-2xl font-bold text-gray-300">2.4%</div>
                      <span className="text-xs text-gray-500">Conversion</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                    <div className="flex-1 bg-[#0EA5E9]/20 border border-[#0EA5E9]/50 rounded-xl p-4 flex flex-col items-center shadow-[0_0_15px_rgba(14,165,233,0.3)]">
                      <span className="text-xs text-[#0EA5E9] mb-2">Variant B (Winner)</span>
                      <div className="text-2xl font-bold text-white">8.7%</div>
                      <span className="text-xs text-[#0EA5E9]">Conversion</span>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* METRICS / TRUST SECTION */}
      

        {/* GROWTH COMPARISON GRAPH & CTA */}
        <section className="py-32 px-4 relative max-w-6xl mx-auto">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg h-96 bg-[#0EA5E9]/20 blur-[150px] pointer-events-none rounded-full" />
          
          <div className="text-center mb-16 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">The Digital <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Advantage</span></h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">See how a strategic digital presence transforms a flatlining business into an exponential growth engine.</p>
          </div>

          <div className="relative z-10 bg-[#0A0F1F] border border-white/10 rounded-3xl p-6 md:p-12 shadow-2xl mb-20 overflow-hidden group">
            {/* Background Grid */}
            <div className="absolute inset-0 opacity-20"
                 style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 40 0 L 0 0 0 40' fill='none' stroke='rgba(255,255,255,0.2)' stroke-width='1'/%3E%3C/svg%3E")` }}>
            </div>
            
            <div className="relative z-10 flex flex-col md:flex-row gap-12 items-center">
              
              {/* SVG GRAPH */}
              <div className="flex-1 w-full relative h-[300px]">
                <svg viewBox="0 0 500 300" className="w-full h-full overflow-visible">
                  {/* Y Axis / X Axis lines */}
                  <line x1="40" y1="260" x2="480" y2="260" stroke="#334155" strokeWidth="2" />
                  <line x1="40" y1="20" x2="40" y2="260" stroke="#334155" strokeWidth="2" />
                  
                  {/* "Without Digital Marketing" Line (Flat/Slow) */}
                  <path d="M 40 240 Q 200 230 460 210" fill="none" stroke="#64748b" strokeWidth="4" strokeDasharray="8 8" />
                  
                  {/* "With Digital Marketing" Line (Exponential) */}
                  <path d="M 40 240 C 200 240, 300 200, 460 40" fill="none" stroke="#0EA5E9" strokeWidth="6" strokeLinecap="round" className="drop-shadow-[0_0_15px_rgba(14,165,233,0.8)]" />
                  
                  {/* Data Points */}
                  <circle cx="460" cy="210" r="6" fill="#64748b" />
                  <circle cx="460" cy="40" r="8" fill="#fff" stroke="#0EA5E9" strokeWidth="4" className="animate-pulse" />
                  
                  {/* Labels */}
                  <text x="470" y="215" fill="#94a3b8" fontSize="12" fontWeight="500">Traditional</text>
                  <text x="470" y="45" fill="#0EA5E9" fontSize="14" fontWeight="bold">With VDT</text>
                  <text x="250" y="280" fill="#64748b" fontSize="12" textAnchor="middle">Time (Months)</text>
                  <text x="20" y="140" fill="#64748b" fontSize="12" textAnchor="middle" transform="rotate(-90 20 140)">Revenue</text>
                </svg>
                
                {/* Floating Indicator */}
                <div className="absolute top-[10%] left-[55%] bg-[#0EA5E9]/20 border border-[#0EA5E9] text-white px-3 py-1.5 rounded-lg text-sm font-bold backdrop-blur-sm animate-bounce shadow-[0_0_20px_rgba(14,165,233,0.4)]">
                  +450% Growth
                </div>
              </div>

              {/* Text Info */}
              <div className="w-full md:w-1/3 space-y-6">
                <div className="bg-white/5 border border-white/10 p-6 rounded-2xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                    <h4 className="text-lg font-bold text-slate-300">Without Digital Strategy</h4>
                  </div>
                  <p className="text-sm text-gray-400">Relying on word-of-mouth and traditional media. Growth is linear, unpredictable, and severely limited by local reach.</p>
                </div>
                
                <div className="bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 p-6 rounded-2xl shadow-[0_0_20px_rgba(14,165,233,0.15)] relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0EA5E9]/0 to-[#0EA5E9]/10"></div>
                  <div className="flex items-center gap-3 mb-3 relative z-10">
                    <div className="w-3 h-3 rounded-full bg-[#0EA5E9] shadow-[0_0_10px_#0EA5E9]"></div>
                    <h4 className="text-lg font-bold text-white">With Digital Growth Agency</h4>
                  </div>
                  <p className="text-sm text-gray-300 relative z-10">Data-driven targeting, viral social presence, and CRO. Growth becomes exponential as algorithms work for you 24/7.</p>
                </div>
              </div>
              
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default DigitalMarketing;
